/**
 * Team vault API client — the CLIENT half of the cloud-authoritative team vault.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SECURITY — READ THIS BEFORE WIRING IT ANYWHERE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. TEAM ITEMS ARE NOT VAULT ITEMS AND MUST NEVER JOIN `vault_list`.
 *
 *    `vaultShareCrypto.js` does two UNSCOPED name lookups over the raw
 *    `vault_list` output (`:154-157`, `:282-285`), each taking the first match:
 *
 *        const envelopes = await invoke('vault_list');
 *        const existing = envelopes.find((it) => it?.name === '__e2e_keypair__');
 *
 *    Merge team items into that list and anyone with team WRITE access can
 *    plant `__e2e_keypair__` or `__e2e_seen_keys__` for every other member:
 *    identity substitution (their published identity becomes the attacker's
 *    key), permanent DoS (the refuse-to-overwrite guard at `:175-184` throws
 *    `keypair_unreadable` forever), TOFU poisoning of the pin store, and
 *    exfiltration of the pin map back into the team vault. And it is invisible,
 *    because `visibleItems` (`vaultStore.js:71`) hides `__`-prefixed names.
 *
 *    Sharper still: the team vault is Cloak-encrypted, so THE SERVER CAN WRITE
 *    IT. Merging would hand the server a path to defeat the chat E2E design —
 *    a capability the personal vault's zero-knowledge model specifically
 *    denies it.
 *
 *    Therefore team items travel their own route (this module) and every
 *    consumer OPTS IN. Nothing here calls `invoke`, and nothing here is
 *    reachable from `vaultShareCrypto`.
 *
 * 2. THE SERVER CAN READ TEAM ITEM BODIES.
 *
 *    They are Cloak-encrypted at rest, not end-to-end encrypted. That is the
 *    deliberate trade for revocation actually working (see the plan §1). UI
 *    copy must never imply otherwise — `src/tests/endToEndClaims.test.js` is
 *    the guard and is not to be amended for this feature.
 *
 * 3. THE ID SPACES ARE DISJOINT BY CONSTRUCTION.
 *
 *    Personal vault ids are opaque Rust-side strings; team item ids are server
 *    integers. A bare team id handed to `vault_get_item` / `ssh_credentials_
 *    for_item` would be a lookup in the WRONG table. So team ids never travel
 *    bare on this side: `teamItemKey()` stamps them `team:<teamId>:<itemId>`,
 *    which no personal id can ever equal, and `parseTeamItemKey()` is the only
 *    way back. Every routing decision keys off that, never off a heuristic.
 */
import { apiClient } from '../utils/apiClient';

/** Prefix that makes a team item id unmistakable for a personal one. */
export const TEAM_ITEM_ID_PREFIX = 'team:';

/** Server caps (mirrored client-side only to fail fast with a readable error). */
export const MAX_TEAM_INDEX_BYTES = 4 * 1024;
export const MAX_TEAM_DATA_BYTES = 90 * 1024;

/**
 * The client-side id for a team item: `team:<teamId>:<itemId>`.
 *
 * Never collides with a personal vault id, so a mis-route is impossible rather
 * than merely unlikely — a personal command handed one of these looks up an id
 * that cannot exist instead of silently resolving somebody else's credential.
 */
export function teamItemKey(teamId, itemId) {
  return `${TEAM_ITEM_ID_PREFIX}${teamId}:${itemId}`;
}

/** `{ teamId, itemId }` for a team key, or null for anything else. */
export function parseTeamItemKey(key) {
  if (typeof key !== 'string' || !key.startsWith(TEAM_ITEM_ID_PREFIX)) return null;
  const rest = key.slice(TEAM_ITEM_ID_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep <= 0 || sep === rest.length - 1) return null;
  return { teamId: rest.slice(0, sep), itemId: rest.slice(sep + 1) };
}

/** True for a `team:<teamId>:<itemId>` key. Personal ids always answer false. */
export function isTeamItemKey(key) {
  return parseTeamItemKey(key) != null;
}

/**
 * The plaintext `index` for a team item.
 *
 * A JS mirror of Rust's `derive_index` (`vault.rs:898-989`) — deliberately the
 * same field set, because `FtpSidebar` renders rows ENTIRELY from `conn.index`
 * (`:216`, `:282`) and a team row that carried a different shape would render
 * blank. The personal vault derives this Rust-side after decrypting; the team
 * vault has no client-side crypto, so the client derives it before the write.
 *
 * `folder` is deliberately ABSENT: a personal folder is a name inside the
 * encrypted body lifted into a local-only index, whereas a team folder is a
 * real server row referenced by `folder_id`. Writing a `folder` key here would
 * put a second, divergent source of truth in a field the server can read.
 *
 * The index is PLAINTEXT TO THE SERVER. Only what the sidebar must render
 * without decrypting belongs in it; the secret stays in `data`.
 */
export function deriveTeamIndex(itemType, data) {
  const d = data && typeof data === 'object' ? data : {};
  const str = (k) => (typeof d[k] === 'string' ? d[k] : undefined);
  const port = (fallback) => {
    const raw = d.port;
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
    if (typeof raw === 'string' && raw.trim() !== '') {
      const n = parseInt(raw.trim(), 10);
      if (Number.isFinite(n)) return n;
    }
    return fallback;
  };

  switch (itemType) {
    case 'password': {
      // `lenient_host` in Rust: the host of the URL, tolerating a missing scheme.
      const url = (str('url') || '').trim();
      let urlHost = null;
      if (url) {
        try {
          urlHost = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`).hostname || null;
        } catch {
          urlHost = null;
        }
      }
      const username = (str('username') || '').trim() ? str('username') : null;
      return { urlHost, username };
    }
    case 'sftp': {
      const raw = str('protocol');
      const protocol = raw === 'sftp' || raw === 'ftp' || raw === 'ftps' ? raw : 'sftp';
      return {
        protocol,
        host: (str('host') || '').trim(),
        port: port(protocol === 'sftp' ? 22 : 21),
        username: str('username') || '',
      };
    }
    // Must match `derive_index`'s arm in src-tauri/src/vault.rs AND the engine
    // list the modal offers. It carried `mariadb` (removed from the product) and
    // lacked `sqlite` (added to it), so a SQLite connection was indexed as
    // mysql:3306 — and for a team item this index is the SERVER-READABLE
    // plaintext, so the wrong engine and an invented port went on the wire.
    // SQLite is a file: no host, no port, no database, matching what `buildData`
    // already refuses to emit. The path stays out of the index deliberately —
    // it is identifying, and this object is not encrypted.
    case 'dbConnection': {
      const raw = str('engine');
      const engine = raw === 'mysql' || raw === 'postgres' || raw === 'sqlite' ? raw : 'mysql';
      if (engine === 'sqlite') return { engine };
      return {
        engine,
        host: (str('host') || '').trim(),
        port: port(engine === 'postgres' ? 5432 : 3306),
        username: str('username') || '',
        database: str('database') || '',
      };
    }
    case 'authHeader':
      return {
        headerName: str('headerName') || '',
        sourceEnv: str('sourceEnv') || null,
      };
    default:
      return {};
  }
}

/**
 * `data` on the wire is an opaque plaintext STRING (the server Cloak-encrypts
 * the column); the client keeps the same object shape the personal vault uses,
 * so JSON is the encoding on both sides.
 */
export function encodeItemData(data) {
  return JSON.stringify(data == null ? {} : data);
}

/**
 * Decode a body the server returned.
 *
 * Throws rather than returning `{}` on garbage: an empty object here would
 * become a connection attempt with a blank password against a production host,
 * which reads to the user as "wrong password" rather than "this item is
 * corrupt". Fail loudly instead.
 */
export function decodeItemData(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    throw new Error('team_vault_data_unreadable');
  }
}

/**
 * The human-readable reason out of a failed team-vault call.
 *
 * The endpoints answer `{"error": msg}`, and 422 answers
 * `{"message": ..., "errors": {...}}` — reading only one of the two threw away
 * every server-supplied reason (the same bug `billingErrorMessage` exists to
 * fix). Field errors are flattened so a 422 says which field, not just "invalid".
 */
export function teamVaultErrorMessage(err, fallback = 'Team vault request failed') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data.error === 'string' && data.error) return data.error;
  const fieldErrors = data.errors && typeof data.errors === 'object' ? data.errors : null;
  if (fieldErrors) {
    const parts = Object.entries(fieldErrors)
      .map(([field, msgs]) => `${field} ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`)
      .filter(Boolean);
    if (parts.length) return `${data.message ? `${data.message}: ` : ''}${parts.join('; ')}`;
  }
  if (typeof data.message === 'string' && data.message) return data.message;
  return err?.message || fallback;
}

/**
 * HTTP status → a token the UI can branch on without leaking existence.
 *
 * The server's convention is deliberate and must not be flattened here: 404
 * means "you cannot read the container" and NEVER confirms the thing exists;
 * 403 means "you can read it but not write it". Collapsing both into
 * "forbidden" would re-introduce the existence oracle the status split exists
 * to prevent.
 */
export function teamVaultErrorToken(err) {
  const status = err?.response?.status;
  if (status === 404) return 'team_vault_not_found';
  if (status === 403) return 'team_vault_read_only';
  if (status === 409) return 'team_vault_conflict';
  if (status === 422) return 'team_vault_invalid';
  if (status === 429) return 'team_vault_rate_limited';
  return '';
}

const base = (teamId) => `/teams/${encodeURIComponent(String(teamId))}/vault`;

const byteLength = (s) =>
  typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(s).length : String(s).length;

function assertWithinCaps(indexObj, dataString) {
  if (indexObj != null && byteLength(JSON.stringify(indexObj)) > MAX_TEAM_INDEX_BYTES) {
    throw new Error('team_vault_index_too_large');
  }
  if (dataString != null && byteLength(dataString) > MAX_TEAM_DATA_BYTES) {
    throw new Error('team_vault_data_too_large');
  }
}

export const teamVaultService = {
  // ── folders ───────────────────────────────────────────────────────────────

  /**
   * `{ folders, root: { can_read, can_write }, can_administer,
   *    can_manage_folders }`.
   *
   * `canManageFolders` answers "may I create/rename/delete a folder in this
   * team", which is FULL write scope on the server (`require_full_write`) and
   * is not derivable from the other two fields: a root-scoped grantee has
   * `root.can_write` and is refused, a whole-vault grantee is not an
   * administrator and is allowed.
   *
   * Falls back to `can_administer` against a server that predates the field —
   * conservative on purpose. An owner or admin, who is who reaches for this in
   * practice, still gets the control; a whole-vault grantee sees it disabled
   * until the server is updated. That is a control that refuses something
   * permitted, which is recoverable; the other direction is a filled-in form
   * thrown away by a 403.
   */
  async listFolders(teamId) {
    const res = await apiClient.get(`${base(teamId)}/folders`);
    const d = res?.data || {};
    return {
      folders: Array.isArray(d.folders) ? d.folders : [],
      root: d.root || { can_read: false, can_write: false },
      canAdminister: !!d.can_administer,
      canManageFolders:
        d.can_manage_folders === undefined ? !!d.can_administer : !!d.can_manage_folders,
    };
  },

  async createFolder(teamId, name) {
    const res = await apiClient.post(`${base(teamId)}/folders`, { name });
    return res?.data;
  },

  async renameFolder(teamId, folderId, name) {
    const res = await apiClient.patch(`${base(teamId)}/folders/${encodeURIComponent(folderId)}`, {
      name,
    });
    return res?.data;
  },

  /**
   * `moveTo` is 'root', a folder id, or null/undefined for "must already be
   * empty" — the server answers 409 in that last case rather than cascading,
   * because deleting a folder full of production credentials must be a
   * deliberate act.
   */
  async deleteFolder(teamId, folderId, moveTo) {
    const qs = moveTo == null ? '' : `?move_to=${encodeURIComponent(moveTo)}`;
    const res = await apiClient.delete(
      `${base(teamId)}/folders/${encodeURIComponent(folderId)}${qs}`
    );
    return res?.data;
  },

  // ── items ─────────────────────────────────────────────────────────────────

  /**
   * Envelopes for the caller's AUTHORIZED view. NEVER includes `data` — the
   * server does not return bodies here at all, which is what keeps a list
   * refresh off the rate-limited, audited body-read path.
   *
   * `folderId`: omit for everything visible, 'root' for the team root, or a
   * folder id.
   */
  async listItems(teamId, folderId) {
    const qs = folderId == null ? '' : `?folder_id=${encodeURIComponent(folderId)}`;
    const res = await apiClient.get(`${base(teamId)}/items${qs}`);
    const items = res?.data?.items;
    return Array.isArray(items) ? items : [];
  },

  /**
   * One item WITH its decrypted body. Rate-limited and audited server-side —
   * reading a production credential is the most audit-worthy action in the
   * product. Call it at the moment of use, never to populate a list.
   */
  async getItem(teamId, itemId) {
    const res = await apiClient.get(`${base(teamId)}/items/${encodeURIComponent(itemId)}`);
    const item = res?.data || {};
    return { ...item, data: decodeItemData(item.data) };
  },

  async createItem(teamId, { itemType, name, folderId = null, data = {} }) {
    const index = deriveTeamIndex(itemType, data);
    const encoded = encodeItemData(data);
    assertWithinCaps(index, encoded);
    const res = await apiClient.post(`${base(teamId)}/items`, {
      item_type: itemType,
      name,
      folder_id: folderId,
      index,
      data: encoded,
    });
    return res?.data;
  },

  /**
   * `version` is REQUIRED — omitting it is a 422 and a stale one is a 409.
   * That is optimistic concurrency doing its job: two admins editing one
   * production password must not be silent last-write-wins.
   */
  async updateItem(teamId, itemId, { version, name, itemType, folderId, data }) {
    if (version == null) throw new Error('team_vault_version_required');
    const payload = { version };
    if (name !== undefined) payload.name = name;
    if (itemType !== undefined) payload.item_type = itemType;
    if (folderId !== undefined) payload.folder_id = folderId;
    if (data !== undefined) {
      const index = deriveTeamIndex(itemType, data);
      const encoded = encodeItemData(data);
      assertWithinCaps(index, encoded);
      payload.index = index;
      payload.data = encoded;
    }
    const res = await apiClient.put(
      `${base(teamId)}/items/${encodeURIComponent(itemId)}`,
      payload
    );
    return res?.data;
  },

  async deleteItem(teamId, itemId) {
    const res = await apiClient.delete(`${base(teamId)}/items/${encodeURIComponent(itemId)}`);
    return res?.data;
  },

  // ── grants (owner/admin only; the server is the authority, not the UI) ─────

  async listGrants(teamId) {
    const res = await apiClient.get(`${base(teamId)}/grants`);
    const grants = res?.data?.grants;
    return Array.isArray(grants) ? grants : [];
  },

  /**
   * Upsert one grant. `scope` is 'vault' | 'root' | 'folder'; `folderId` is
   * required for and only meaningful to 'folder'.
   *
   * The scope enum exists because NULL was overloaded: in items a null
   * folder_id means "team root" (a real place holding items), while in grants
   * it meant "the whole vault". Without the enum there was no way to grant
   * root-only, and asking for it silently granted everything.
   */
  async putGrant(teamId, { userId, scope, folderId = null, permission }) {
    const payload = { user_id: userId, scope, permission };
    if (scope === 'folder') payload.folder_id = folderId;
    const res = await apiClient.put(`${base(teamId)}/grants`, payload);
    return res?.data;
  },

  async deleteGrant(teamId, grantId) {
    const res = await apiClient.delete(`${base(teamId)}/grants/${encodeURIComponent(grantId)}`);
    return res?.data;
  },
};

export default teamVaultService;
